import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TableConnector, UpsertConnectorRequest } from '@loader/shared-types';

import { queryKeys } from '@/lib/queryKeys';
import { throwApiError, useRpc } from '@/lib/rpc';

/** Poll interval when connectors are awaiting their initial sync */
const PENDING_SYNC_POLL_MS = 5_000;

/**
 * Fetch all connectors for the current workspace.
 * Polls every 5s while any enabled connector has never synced.
 */
export function useConnectorListQuery() {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.connectors.list,
    queryFn: async (): Promise<TableConnector[]> => {
      const res = await rpc.api.private['table-connectors'].$get();
      if (!res.ok) await throwApiError(res, 'Failed to fetch connectors');
      const json = (await res.json()) as { connectors: TableConnector[] };
      return json.connectors;
    },
    staleTime: 30_000,
    refetchInterval: (query) => {
      const connectors = query.state.data;
      const hasPending = connectors?.some((c) => c.enabled && !c.last_synced_at);
      return hasPending ? PENDING_SYNC_POLL_MS : false;
    },
  });
}

/**
 * Fetch a single connector by table ID.
 */
export function useConnectorQuery(tableId: string | undefined) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.connectors.detail(tableId ?? ''),
    queryFn: async (): Promise<TableConnector | null> => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['table-connectors'][':tableId'].$get({
        param: { tableId: tableId! },
      });
      if (!res.ok) await throwApiError(res, 'Failed to fetch connector');
      const json = (await res.json()) as { connector: TableConnector | null };
      return json.connector;
    },
    enabled: !!tableId,
  });
}

/**
 * Create or update a connector for a table.
 */
export function useUpsertConnector() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableId,
      data,
    }: {
      tableId: string;
      data: UpsertConnectorRequest;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['table-connectors'][':tableId'].$put({
        param: { tableId },
        json: data,
      });
      if (!res.ok) await throwApiError(res, 'Failed to save connector');
      const json = (await res.json()) as { connector: TableConnector };
      return json.connector;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
    },
  });
}

/**
 * Toggle a connector's enabled state.
 */
export function useToggleConnector() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tableId,
      enabled,
    }: {
      tableId: string;
      enabled: boolean;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['table-connectors'][':tableId'].toggle.$patch({
        param: { tableId },
        json: { enabled },
      });
      if (!res.ok) await throwApiError(res, 'Failed to toggle connector');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
    },
  });
}

/**
 * Delete (clear) a connector from a table.
 */
export function useDeleteConnector() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tableId: string) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['table-connectors'][':tableId'].$delete({
        param: { tableId },
      });
      if (!res.ok) await throwApiError(res, 'Failed to delete connector');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}
